/** True when another tab or request saved the conversation first. */
export function isOnboardingConversationConflict(error: unknown): boolean {
  return (error as { status?: unknown } | null)?.status === 409;
}

/**
 * A lost HTTP response does not prove an upload failed. The upload request id
 * is persisted on its user transcript entry, so reloading the conversation can
 * tell the browser whether that exact attempt actually landed.
 */
export function onboardingRequestWasSaved(
  transcript: ReadonlyArray<{ role: "user" | "assistant"; requestId?: string }>,
  requestId: string,
): boolean {
  return transcript.some((message) => message.role === "user" && message.requestId === requestId);
}

export interface OnboardingPromptExpectation {
  questionId: string | null;
  promptRevision: string;
}

/** New clients opt into the server's prompt guard only after they have received
 * an exact question token. This keeps a rolling web/API deploy compatible while
 * preventing a typed answer from landing on a different question. */
export function onboardingPromptGuard(
  expectation: OnboardingPromptExpectation | null,
): Record<string, unknown> {
  if (!expectation) return {};
  return {
    promptGuardVersion: 1,
    expectedQuestionId: expectation.questionId,
    expectedPromptRevision: expectation.promptRevision,
  };
}

/** A saved upload is server-idempotent. One immediate retry covers a dropped
 * response without making the client repeat the step or applying the file
 * twice. The caller still reloads authoritative state before showing failure. */
export async function retryOnboardingImportOnce<T>(attempt: () => Promise<T>): Promise<T> {
  try {
    return await attempt();
  } catch (error) {
    const status = (error as { status?: unknown } | null)?.status;
    // A clear client/permission/file-format response will not improve one
    // millisecond later. Retry only a lost response, a server failure, or a
    // save conflict that may now have an idempotent replay ready.
    if (typeof status === "number" && status >= 400 && status < 500 && status !== 409) throw error;
    return attempt();
  }
}

export interface CachedCatalogInterviewProgress {
  started: boolean;
  resolvedDecisionIds: string[];
  reviewReached: boolean;
}

export interface CatalogInterviewQueryData {
  journey: {
    catalogInterview?: CachedCatalogInterviewProgress;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/**
 * Keep the resume query current as soon as a walkthrough checkpoint saves.
 * React Query can return old cached data immediately when Categorize remounts;
 * without this merge, its one-shot hydration can consume that old checkpoint
 * before the background refetch returns and re-ask questions already answered.
 */
export function withSavedCatalogInterviewProgress(
  current: CatalogInterviewQueryData | undefined,
  progress: CachedCatalogInterviewProgress,
): CatalogInterviewQueryData {
  return {
    ...(current ?? {}),
    journey: {
      ...(current?.journey ?? {}),
      catalogInterview: {
        ...progress,
        resolvedDecisionIds: [...progress.resolvedDecisionIds],
      },
    },
  };
}
